<script setup>
defineProps({
  filtros: { type: Object, required: true },
  gruposDisponiveis: { type: Array, default: () => [] },
  temFiltroAtivo: { type: Boolean, default: false }
})

const emit = defineEmits([
  'aplicar-filtros',
  'limpar-filtros',
  'alternar-faixa-valor',
  'alternar-grupo',
  'update:buscaNumero',
  'update:buscaResumo'
])

const faixasValor = [
  { value: 'ate10mil', label: 'Até R$ 10 mil' },
  { value: 'de10a30mil', label: 'R$ 10 a 30 mil' },
  { value: 'acima30mil', label: '> R$ 30 mil' }
]
</script>

<template>
  <div class="filtro-inline-row">
    <!-- Filtro: Nº PO / Referência -->
    <div class="filtro-group filtro-numero">
      <label class="form-label filtro-label">Nº PO / Referência</label>
      <input
        type="text"
        class="form-control form-control-sm"
        :value="filtros.buscaNumero"
        @input="emit('update:buscaNumero', $event.target.value)"
        placeholder="Nº ou referência..."
        @keydown.enter.prevent="emit('aplicar-filtros')"
      >
    </div>

    <!-- Filtro: Resumo -->
    <div class="filtro-group filtro-resumo">
      <label class="form-label filtro-label">Resumo</label>
      <input
        type="text"
        class="form-control form-control-sm"
        :value="filtros.buscaResumo"
        @input="emit('update:buscaResumo', $event.target.value)"
        placeholder="Filtrar por resumo..."
        @keydown.enter.prevent="emit('aplicar-filtros')"
      >
    </div>

    <!-- Filtro: Faixa de Valor -->
    <div class="filtro-group filtro-valor">
      <label class="form-label filtro-label">Valor Negociado</label>
      <div class="d-flex flex-wrap gap-1">
        <button
          v-for="faixa in faixasValor"
          :key="faixa.value"
          class="btn btn-sm"
          :class="filtros.faixaValor === faixa.value ? 'btn-primary' : 'btn-outline-secondary'"
          @click="emit('alternar-faixa-valor', faixa.value)"
        >
          {{ faixa.label }}
        </button>
      </div>
    </div>

    <!-- Filtro: Grupo Aprovador (badges) -->
    <div class="filtro-group filtro-grupo" v-if="gruposDisponiveis.length > 0">
      <label class="form-label filtro-label">Grupo</label>
      <div class="d-flex flex-wrap gap-1">
        <button
          v-for="grupo in gruposDisponiveis"
          :key="grupo.id"
          class="badge rounded-pill px-3 py-2 text-decoration-none border-0"
          :class="filtros.idGrupoAprovador === grupo.id ? 'text-bg-info' : 'text-bg-light text-body-secondary border'"
          @click="emit('alternar-grupo', grupo.id)"
        >
          {{ grupo.nome }}
        </button>
      </div>
    </div>

    <!-- Botões de ação -->
    <div class="filtro-group filtro-acoes">
      <div class="d-flex gap-1">
        <button
          class="btn btn-sm btn-outline-secondary"
          @click="emit('limpar-filtros')"
          :disabled="!temFiltroAtivo"
          title="Limpar filtros"
        >
          <i class="bi bi-x-lg"></i>
        </button>
        <button class="btn btn-sm btn-primary" @click="emit('aplicar-filtros')">
          <i class="bi bi-search"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filtro-inline-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}

.filtro-group {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.filtro-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color);
  margin-bottom: 0;
}

.filtro-numero {
  flex: 1 1 140px;
  min-width: 120px;
}

.filtro-resumo {
  flex: 1 1 160px;
  min-width: 130px;
}

.filtro-valor {
  flex: 0 1 auto;
}

.filtro-grupo {
  flex: 1 1 100%;
}

.filtro-acoes {
  flex: 0 0 auto;
}

.filtro-grupo .badge {
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.filtro-grupo .badge.text-bg-light {
  background-color: color-mix(in srgb, var(--bs-secondary-bg) 60%, transparent) !important;
  color: var(--bs-body-color) !important;
  border-color: var(--bs-border-color) !important;
}

.filtro-grupo .badge:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}

.filtro-valor .btn {
  font-size: 0.72rem;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}

@media (max-width: 576px) {
  .filtro-inline-row { gap: 0.4rem; }
  .filtro-numero, .filtro-resumo { flex: 1 1 100%; min-width: 0; }
  .filtro-valor { flex: 1 1 100%; }
  .filtro-valor .btn { flex: 1; text-align: center; }
  .filtro-grupo .badge { font-size: 0.7rem; padding: 0.25rem 0.6rem; }
  .filtro-acoes { flex: 1 1 100%; }
  .filtro-acoes .d-flex { justify-content: flex-end; }
}
</style>
